// ─────────────────────────────────────────────────────────────
//  MacroManager.js  –  Office JS Word Add-in version
//  Storage priority:
//    1. S3  (via Lambda presigned URLs – same bucket as Chrome ext)
//    2. Office roamingSettings  (cross-device fallback)
//    3. localStorage  (offline fallback)
// ─────────────────────────────────────────────────────────────

const LAMBDA_PRESIGNED_URL =
  'https://ugt7zmmc5up3u3ewv2bkmaa7lq0vjsko.lambda-url.us-east-1.on.aws/';
const S3_BUCKET      = 'notes-and-abbreviations';
const S3_MAX_RETRIES = 3;

// ── Trie (unchanged) ──────────────────────────────────────────
class TrieNode {
  constructor() {
    this.children = new Map();
    this.isEnd    = false;
    this.value    = null;
  }
}

class Trie {
  constructor() { this.root = new TrieNode(); }

  insert(key, value) {
    let node = this.root;
    for (const char of key.toLowerCase()) {
      if (!node.children.has(char)) node.children.set(char, new TrieNode());
      node = node.children.get(char);
    }
    node.isEnd  = true;
    node.value  = value;
  }

  findMatches(text) {
    const matches = [];
    text = text.toLowerCase();
    for (let i = 0; i < text.length; i++) {
      let node = this.root, j = i, lastMatch = null;
      while (j < text.length && node.children.has(text[j])) {
        node = node.children.get(text[j]);
        if (node.isEnd) lastMatch = { start: i, end: j + 1, value: node.value };
        j++;
      }
      if (lastMatch) matches.push(lastMatch);
    }
    return matches.sort((a, b) => b.end - a.end);
  }
}

// ─────────────────────────────────────────────────────────────
export class MacroManager {
  constructor(configManager, notificationManager) {
    this.configManager       = configManager;
    this.notificationManager = notificationManager;

    this.macros      = new Map();
    this.trie        = new Trie();
    this.regexCache  = new Map();
    this.searchIndex = new Map();
    this.lastS3Hash  = null;
    this.isLoading   = false;

    this.eventListeners = {};
    this.macroWindow    = null;
    this.searchTimeout  = null;

    this._injectStyles();
  }

  // ── Event system ─────────────────────────────────────────────
  on(event, cb) {
    if (!this.eventListeners[event]) this.eventListeners[event] = [];
    this.eventListeners[event].push(cb);
  }
  emit(event, data) {
    (this.eventListeners[event] || []).forEach(cb => cb(data));
  }

  // ── Public init ──────────────────────────────────────────────
  async init() {
    try {
      await this.loadMacrosFromS3();
      this.buildTrieAndIndex();
      console.log(`MacroManager initialized with ${this.macros.size} abbreviations`);
    } catch (err) {
      console.error('MacroManager init failed:', err);
      this.notificationManager?.show('Հրահանգները բեռնել չհաջողվեց', 'error');
    }
  }

  buildTrieAndIndex() {
    this.trie = new Trie();
    this.searchIndex.clear();
    this.regexCache.clear();

    for (const [key, value] of this.macros) {
      this.trie.insert(key, value);
      this.searchIndex.set(key.toLowerCase(),   value);
      this.searchIndex.set(value.toLowerCase(), value);

      const escaped = this.escapeRegex(key);
      this.regexCache.set(
        key,
        new RegExp(`(?<=\\s|^)${escaped}(?=\\s|[.,!?;]|$)`, 'gi')
      );
    }
  }

  // ─────────────────────────────────────────────────────────────
  //  S3 LAYER  (mirrors background.js logic directly in the page)
  //  No chrome.runtime.sendMessage – we call Lambda directly via
  //  fetch(), just like background.js does, since the taskpane
  //  is a regular web page with full fetch access.
  // ─────────────────────────────────────────────────────────────

  /** Ask Lambda for a presigned URL then return it */
  async _getPresignedUrl(key, operation) {
    const res = await fetch(LAMBDA_PRESIGNED_URL, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ key, bucket: S3_BUCKET, operation }),
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Lambda error ${res.status}: ${text}`);
    }

    const data = await res.json();
    return data.url;
  }

  /** S3 object key for a given user's abbreviations */
  _s3Key(username) {
    return `users/${username}/abbreviations/${username}_abbreviations.json`;
  }

  /** Unicode-safe content hash – identical algorithm to background.js */
  _createContentHash(content) {
    try {
      const bytes  = new TextEncoder().encode(content);
      const binary = Array.from(bytes, b => String.fromCharCode(b)).join('');
      return btoa(binary).slice(0, 16);
    } catch {
      let h = 0;
      for (let i = 0; i < content.length; i++) {
        h = ((h << 5) - h + content.charCodeAt(i)) | 0;
      }
      return Math.abs(h).toString(36).slice(0, 16);
    }
  }

  // ── LOAD from S3 ─────────────────────────────────────────────
  async loadMacrosFromS3() {
    const username = this.configManager?.getDoctorUsername?.();
    if (!username) {
      console.warn('No username available – falling back to local cache');
      await this._loadFromLocalFallback();
      return;
    }

    this.isLoading = true;
    const key      = this._s3Key(username);
    let   attempt  = 0;

    while (attempt < S3_MAX_RETRIES) {
      try {
        console.log(
          `Loading abbreviations for "${username}" from S3 (attempt ${attempt + 1}/${S3_MAX_RETRIES})`
        );

        const presignedUrl = await this._getPresignedUrl(key, 'get_object');

        const res = await fetch(presignedUrl, {
          method:  'GET',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma':        'no-cache',
          },
        });

        if (res.ok) {
          const content = await res.text();
          const hash    = this._createContentHash(content);

          if (hash !== this.lastS3Hash) {
            this.lastS3Hash = hash;
            this._parseMacrosFromContent(content);
            console.log(`✅ Loaded ${this.macros.size} abbreviations from S3`);

            if (this.macros.size > 0) {
              this.notificationManager?.show(
                `${this.macros.size} հրահանգներ բեռնվել են`,
                'success'
              );
            }

            // Mirror to local fallbacks so offline still works
            await this._cacheLocally(content, hash, username);
          } else {
            console.log('S3 content unchanged (hash match) – skipping re-parse');
          }

          this.isLoading = false;
          return;

        } else if (res.status === 404) {
          console.log('No abbreviations file on S3 yet – starting fresh');
          this.macros.clear();
          this.isLoading = false;
          return;

        } else {
          throw new Error(`S3 GET ${res.status} ${res.statusText}`);
        }

      } catch (err) {
        attempt++;
        console.error(`S3 load attempt ${attempt} failed:`, err.message);

        if (attempt >= S3_MAX_RETRIES) {
          console.warn('All S3 retries exhausted – using local cache');
          await this._loadFromLocalFallback();
          this.isLoading = false;
          return;
        }

        await new Promise(r => setTimeout(r, 1000 * attempt));
      }
    }

    this.isLoading = false;
  }

  _parseMacrosFromContent(content) {
    this.macros.clear();
    if (!content?.trim()) return;

    try {
      const parsed = JSON.parse(content);
      if (parsed && typeof parsed === 'object') {
        for (const [k, v] of Object.entries(parsed)) {
          if (typeof k === 'string' && typeof v === 'string') {
            this.macros.set(k, v);
          }
        }
      }
      console.log(`Parsed ${this.macros.size} abbreviations`);
    } catch (err) {
      console.error('Failed to parse abbreviations JSON:', err);
    }
  }

  // ── SAVE to S3 ───────────────────────────────────────────────
  async saveMacrosToS3() {
    const username = this.configManager?.getDoctorUsername?.();
    if (!username) {
      this.notificationManager?.show('Մուտքագրեք Ձեր օգտանունը', 'error');
      return false;
    }

    const key     = this._s3Key(username);
    const content = JSON.stringify(Object.fromEntries(this.macros));
    let   attempt = 0;

    while (attempt < S3_MAX_RETRIES) {
      try {
        console.log(
          `Saving ${this.macros.size} abbreviations to S3 (attempt ${attempt + 1}/${S3_MAX_RETRIES})`
        );

        const presignedUrl = await this._getPresignedUrl(key, 'put_object');

        const res = await fetch(presignedUrl, {
          method:  'PUT',
          body:    content,
          headers: { 'Content-Type': 'application/json; charset=utf-8' },
        });

        if (res.ok) {
          const hash      = this._createContentHash(content);
          this.lastS3Hash = hash;
          console.log(`✅ Saved ${this.macros.size} abbreviations to S3`);

          await this._cacheLocally(content, hash, username);
          this.notificationManager?.show('Հրահանգները պահպանվեցին', 'success');
          return true;

        } else {
          throw new Error(`S3 PUT ${res.status} ${res.statusText}`);
        }

      } catch (err) {
        attempt++;
        console.error(`S3 save attempt ${attempt} failed:`, err.message);

        if (attempt >= S3_MAX_RETRIES) {
          // Work isn't lost – mirror locally so next sync can push it
          await this._cacheLocally(content, null, username);
          this.notificationManager?.show(
            'S3 պահպանումը ձախողվեց – պահված տեղայնորեն',
            'warning'
          );
          return false;
        }

        await new Promise(r => setTimeout(r, 1000 * attempt));
      }
    }

    return false;
  }

  // ─────────────────────────────────────────────────────────────
  //  LOCAL CACHES  (Office roamingSettings + localStorage)
  //  These are mirrors only – S3 is the source of truth.
  // ─────────────────────────────────────────────────────────────

  async _cacheLocally(content, hash, username) {
    // 1. Office roamingSettings (syncs across machines automatically)
    try {
      if (typeof Office !== 'undefined' && Office?.context?.roamingSettings) {
        const rs = Office.context.roamingSettings;
        rs.set('doccy_macros',      content);
        rs.set('doccy_macros_hash', hash || '');
        await new Promise((resolve, reject) => {
          rs.saveAsync(result => {
            if (result.status === Office.AsyncResultStatus.Succeeded) resolve();
            else reject(result.error);
          });
        });
        console.log('Mirrored to Office roamingSettings');
      }
    } catch (err) {
      console.warn('roamingSettings mirror failed:', err.message);
    }

    // 2. localStorage (fastest offline read)
    try {
      const u = username || 'unknown';
      localStorage.setItem(`doccy_macros_${u}`,      content);
      localStorage.setItem(`doccy_macros_hash_${u}`, hash || '');
      console.log('Mirrored to localStorage');
    } catch (err) {
      console.warn('localStorage mirror failed:', err.message);
    }
  }

  async _loadFromLocalFallback() {
    const username = this.configManager?.getDoctorUsername?.() || 'unknown';

    // 1. Office roamingSettings
    try {
      if (typeof Office !== 'undefined' && Office?.context?.roamingSettings) {
        const rs  = Office.context.roamingSettings;
        const raw = rs.get('doccy_macros');
        if (raw) {
          this._parseMacrosFromContent(raw);
          this.lastS3Hash = rs.get('doccy_macros_hash') || null;
          console.log(`📦 Loaded ${this.macros.size} abbreviations from roamingSettings`);
          this.notificationManager?.show(
            `${this.macros.size} հրահանգներ (Office sync)`, 'info'
          );
          return;
        }
      }
    } catch (err) {
      console.warn('roamingSettings load failed:', err.message);
    }

    // 2. localStorage
    try {
      const raw = localStorage.getItem(`doccy_macros_${username}`);
      if (raw) {
        this._parseMacrosFromContent(raw);
        this.lastS3Hash = localStorage.getItem(`doccy_macros_hash_${username}`) || null;
        console.log(`📦 Loaded ${this.macros.size} abbreviations from localStorage`);
        this.notificationManager?.show(
          `${this.macros.size} հրահանգներ (տեղային)`, 'info'
        );
        return;
      }
    } catch (err) {
      console.warn('localStorage load failed:', err.message);
    }

    console.log('No local cache found – starting fresh');
    this.notificationManager?.show('Հրահանգներ չկան պահպանված', 'info');
  }

  // ─────────────────────────────────────────────────────────────
  //  CORE CRUD
  //  Each write first pulls the latest from S3 so concurrent
  //  edits (e.g. Chrome ext open on another machine) don't get
  //  silently overwritten – same pattern as the Chrome extension.
  // ─────────────────────────────────────────────────────────────

  async addMacro(key, value) {
    if (!key?.trim() || !value?.trim()) return false;

    await this.loadMacrosFromS3();               // pull latest before write
    this.macros.set(key.trim(), value.trim());
    this.buildTrieAndIndex();

    const ok = await this.saveMacrosToS3();
    if (ok) {
      this.populateMacroList();
      this.notificationManager?.show(`Ավելացվեց «${key}»`, 'success');
    }
    return ok;
  }

  async updateMacro(oldKey, newKey, newValue) {
    if (!newKey?.trim() || !newValue?.trim()) return false;

    await this.loadMacrosFromS3();
    if (oldKey !== newKey) this.macros.delete(oldKey);
    this.macros.set(newKey.trim(), newValue.trim());
    this.buildTrieAndIndex();

    const ok = await this.saveMacrosToS3();
    if (ok) {
      this.populateMacroList();
      this.notificationManager?.show(`Թարմացվեց «${newKey}»`, 'success');
    }
    return ok;
  }

  async deleteMacro(key) {
    if (!this.macros.has(key)) return false;

    await this.loadMacrosFromS3();
    this.macros.delete(key);
    this.searchIndex.delete(key.toLowerCase());
    this.regexCache.delete(key);
    this.buildTrieAndIndex();

    const ok = await this.saveMacrosToS3();
    if (ok) {
      this.populateMacroList();
      this.notificationManager?.show(`Ջնջվեց «${key}»`, 'success');
    }
    return ok;
  }

  /** Force a fresh pull from S3 (ignores cached hash) */
  async reloadAbbreviations() {
    console.log('Manual S3 reload requested...');
    this.lastS3Hash = null;          // clear hash so content is always re-parsed
    await this.loadMacrosFromS3();
    this.buildTrieAndIndex();
    if (this.macroWindow) this.populateMacroList();
  }

  // ─────────────────────────────────────────────────────────────
  //  TRANSCRIPTION PROCESSING
  // ─────────────────────────────────────────────────────────────

  processTranscription(transcription) {
    if (!transcription || this.macros.size === 0) return transcription;

    const normalizedText = transcription.replace(/\s+/g, ' ').trim();
    let   processedText  = normalizedText;

    const matches = this.trie.findMatches(normalizedText);
    console.log(`Processing "${normalizedText}" – ${matches.length} candidate(s)`);

    for (const match of matches) {
      // Find the original-case key so the regex cache lookup works
      const originalKey = [...this.macros.keys()].find(
        k => k.toLowerCase() === normalizedText.slice(match.start, match.end).toLowerCase()
      );
      const regex = originalKey ? this.regexCache.get(originalKey) : null;
      if (!regex) continue;

      regex.lastIndex = 0;
      const before = processedText;
      processedText = processedText.replace(regex, match.value);

      if (processedText !== before) {
        console.log(`🎯 Expanded: "${originalKey}" → "${match.value}"`);
      }
    }

    return processedText;
  }

  // ─────────────────────────────────────────────────────────────
  //  UI  (modal overlay injected into the taskpane DOM)
  // ─────────────────────────────────────────────────────────────

  showMacroManager() {
    let overlay = document.getElementById('doccy-macro-overlay');
    if (overlay) {
      overlay.style.display = 'flex';
      this.populateMacroList();
      return;
    }

    overlay = document.createElement('div');
    overlay.id = 'doccy-macro-overlay';
    overlay.innerHTML = `
      <div class="dmm-panel">
        <div class="dmm-header">
          <span class="dmm-title">⚡ Հրահանգներ</span>
          <div style="display:flex;gap:8px;align-items:center;">
            <button class="dmm-icon-btn" id="dmm-reload" title="Sync with S3">🔄</button>
            <button class="dmm-close-btn" id="dmm-close" title="Փակել">✕</button>
          </div>
        </div>

        <div class="dmm-search-row">
          <input type="text" id="dmm-search" class="dmm-input"
            placeholder="🔍 Որոնել հրահանգ..."/>
        </div>

        <div class="dmm-add-form">
          <div class="dmm-form-row">
            <input type="text" id="dmm-new-key" class="dmm-input dmm-half"
              placeholder="Կրճատ ձև  (օր. «ԱՄ»)" maxlength="50"/>
            <button class="dmm-btn dmm-btn-primary" id="dmm-add-btn">+ Ավելացնել</button>
          </div>
          <textarea id="dmm-new-value" class="dmm-textarea" rows="2"
            placeholder="Ամբողջ արտահայտություն (օր. «Արյան ճնշում»)"></textarea>
        </div>

        <div id="doccy-macro-list" class="dmm-list"></div>
        <div class="dmm-footer" id="dmm-footer"></div>
      </div>
    `;

    document.body.appendChild(overlay);

    // Wire up controls
    document.getElementById('dmm-close')
      .addEventListener('click', () => { overlay.style.display = 'none'; });

    overlay.addEventListener('click', e => {
      if (e.target === overlay) overlay.style.display = 'none';
    });

    document.getElementById('dmm-reload')
      .addEventListener('click', () => this.reloadAbbreviations());

    document.getElementById('dmm-add-btn')
      .addEventListener('click', () => this._handleAdd());

    document.getElementById('dmm-new-key')
      .addEventListener('keydown', e => { if (e.key === 'Enter') this._handleAdd(); });

    document.getElementById('dmm-search')
      .addEventListener('input', e => {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => this.populateMacroList(e.target.value), 200);
      });

    this.populateMacroList();
  }

  _handleAdd() {
    const keyEl = document.getElementById('dmm-new-key');
    const valEl = document.getElementById('dmm-new-value');
    const key   = keyEl?.value?.trim();
    const val   = valEl?.value?.trim();

    if (!key) { this.notificationManager?.show('Կրճատ ձևը դատարկ է', 'error');           keyEl?.focus(); return; }
    if (!val) { this.notificationManager?.show('Ամբողջ արտահայտությունը դատարկ է', 'error'); valEl?.focus(); return; }

    this.addMacro(key, val).then(() => {
      if (keyEl) keyEl.value = '';
      if (valEl) valEl.value = '';
      keyEl?.focus();
    });
  }

  populateMacroList(searchTerm = '') {
    const container = document.getElementById('doccy-macro-list');
    const footer    = document.getElementById('dmm-footer');
    if (!container) return;

    container.innerHTML = '';
    const term = searchTerm.toLowerCase().trim();

    const filtered = term
      ? Array.from(this.macros.entries()).filter(
          ([k, v]) => k.toLowerCase().includes(term) || v.toLowerCase().includes(term)
        )
      : Array.from(this.macros.entries());

    filtered.sort((a, b) => a[0].localeCompare(b[0]));

    if (footer) footer.textContent = filtered.length ? `${filtered.length} հրահանգ` : '';

    if (filtered.length === 0) {
      container.innerHTML = term
        ? `<div class="dmm-empty"><div class="dmm-empty-icon">🔍</div><p>Արդյունք չի գտնվել</p></div>`
        : `<div class="dmm-empty"><div class="dmm-empty-icon">📋</div><p>Հրահանգներ դեռ չկան</p><small>Ավելացրեք վերևի դաշտից</small></div>`;
      return;
    }

    const PAGE = 30;
    filtered.slice(0, PAGE).forEach(([k, v], i) =>
      container.appendChild(this.createMacroItem(k, v, i))
    );

    if (filtered.length > PAGE) {
      const btn = document.createElement('button');
      btn.className   = 'dmm-load-more';
      btn.textContent = `Ցույց տալ ևս ${filtered.length - PAGE}`;
      btn.addEventListener('click', () => {
        btn.remove();
        filtered.slice(PAGE).forEach(([k, v], i) =>
          container.appendChild(this.createMacroItem(k, v, PAGE + i))
        );
      });
      container.appendChild(btn);
    }
  }

  createMacroItem(key, value) {
    const { text: preview, isTruncated } = this.getPreviewText(value);

    const item = document.createElement('div');
    item.className   = 'dmm-item';
    item.dataset.key = key;

    item.innerHTML = `
      <div class="dmm-item-view">
        <div class="dmm-item-key">${this._esc(key)}</div>
        <div class="dmm-item-value" title="${isTruncated ? this._esc(value) : ''}">
          ${this._esc(preview)}
        </div>
        <div class="dmm-item-actions">
          <button class="dmm-icon-btn dmm-edit-btn"   title="Խմբագրել">✏️</button>
          <button class="dmm-icon-btn dmm-delete-btn" title="Ջնջել">🗑️</button>
        </div>
      </div>

      <div class="dmm-item-edit" style="display:none;">
        <input  type="text" class="dmm-input   dmm-edit-key"
          value="${this._esc(key)}" maxlength="50" placeholder="Կրճատ ձև"/>
        <textarea           class="dmm-textarea dmm-edit-value"
          rows="2" placeholder="Ամբողջ արտահայտություն">${this._esc(value)}</textarea>
        <div class="dmm-edit-btns">
          <button class="dmm-btn dmm-btn-primary dmm-save-btn">💾 Պահպանել</button>
          <button class="dmm-btn dmm-btn-cancel  dmm-cancel-btn">Չեղարկել</button>
        </div>
      </div>
    `;

    item.querySelector('.dmm-edit-btn').addEventListener('click', () => {
      item.querySelector('.dmm-item-view').style.display = 'none';
      item.querySelector('.dmm-item-edit').style.display = 'block';
      item.querySelector('.dmm-edit-key').focus();
    });

    item.querySelector('.dmm-cancel-btn').addEventListener('click', () => {
      item.querySelector('.dmm-item-edit').style.display = 'none';
      item.querySelector('.dmm-item-view').style.display = 'flex';
    });

    item.querySelector('.dmm-save-btn').addEventListener('click', () => {
      const nk = item.querySelector('.dmm-edit-key').value.trim();
      const nv = item.querySelector('.dmm-edit-value').value.trim();
      if (!nk || !nv) { this.notificationManager?.show('Դաշտերը դատարկ են', 'error'); return; }
      this.updateMacro(key, nk, nv);
    });

    item.querySelector('.dmm-delete-btn').addEventListener('click', () => {
      if (confirm(`Ջնջե՞լ «${key}» հրահանգը`)) this.deleteMacro(key);
    });

    return item;
  }

  // ─────────────────────────────────────────────────────────────
  //  UTILITIES
  // ─────────────────────────────────────────────────────────────

  getPreviewText(value, maxSentences = 2, maxChars = 100) {
    const sentences = value.match(/[^.!?]+[.!?]*/g) || [value];
    const joined    = sentences.slice(0, maxSentences).join(' ').trim();
    const preview   = joined.length > maxChars
      ? joined.substring(0, maxChars).trim() + '...'
      : joined;
    return { text: preview, isTruncated: preview !== value };
  }

  escapeRegex(str) { return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }

  _esc(str) {
    return String(str)
      .replace(/&/g,  '&amp;')
      .replace(/</g,  '&lt;')
      .replace(/>/g,  '&gt;')
      .replace(/"/g,  '&quot;')
      .replace(/'/g,  '&#039;');
  }

  getAbbreviationsCount() { return this.macros.size; }
  getAllAbbreviations()    { return Array.from(this.macros.entries()); }

  // ─────────────────────────────────────────────────────────────
  //  CSS  (scoped dmm- prefix, injected once)
  // ─────────────────────────────────────────────────────────────

  _injectStyles() {
    if (document.getElementById('doccy-macro-styles')) return;
    const s = document.createElement('style');
    s.id = 'doccy-macro-styles';
    s.textContent = `
      #doccy-macro-overlay {
        position:fixed; inset:0; background:rgba(0,0,0,.45);
        display:flex; align-items:flex-start; justify-content:center;
        z-index:9999; padding:12px; box-sizing:border-box;
      }
      .dmm-panel {
        background:#fff; border-radius:12px; width:100%; max-width:480px;
        max-height:calc(100vh - 24px); display:flex; flex-direction:column;
        box-shadow:0 8px 32px rgba(0,0,0,.22); overflow:hidden;
        font-family:'Segoe UI',Arial,sans-serif; font-size:13px;
      }
      .dmm-header {
        display:flex; align-items:center; justify-content:space-between;
        background:#1e3a5f; color:#fff; padding:12px 16px; flex-shrink:0;
      }
      .dmm-title { font-size:15px; font-weight:600; letter-spacing:.3px; }
      .dmm-close-btn {
        background:none; border:none; color:rgba(255,255,255,.8);
        cursor:pointer; font-size:16px; padding:2px 6px; border-radius:4px; transition:background .15s;
      }
      .dmm-close-btn:hover { background:rgba(255,255,255,.15); color:#fff; }
      .dmm-search-row { padding:10px 12px 4px; flex-shrink:0; }
      .dmm-add-form   { padding:4px 12px 10px; border-bottom:1px solid #e5e7eb; flex-shrink:0; }
      .dmm-form-row   { display:flex; gap:8px; margin-bottom:6px; }
      .dmm-half       { flex:1; min-width:0; }
      .dmm-input {
        width:100%; padding:7px 10px; border:1.5px solid #d1d5db; border-radius:7px;
        font-size:13px; outline:none; box-sizing:border-box; transition:border-color .15s; color:#111827;
      }
      .dmm-input:focus { border-color:#1e3a5f; }
      .dmm-textarea {
        width:100%; padding:7px 10px; border:1.5px solid #d1d5db; border-radius:7px;
        font-size:13px; outline:none; box-sizing:border-box; resize:vertical;
        transition:border-color .15s; color:#111827; font-family:inherit;
      }
      .dmm-textarea:focus { border-color:#1e3a5f; }
      .dmm-btn {
        padding:7px 14px; border:none; border-radius:7px; cursor:pointer;
        font-size:13px; font-weight:500; white-space:nowrap; transition:background .15s;
      }
      .dmm-btn-primary { background:#1e3a5f; color:#fff; }
      .dmm-btn-primary:hover { background:#16304f; }
      .dmm-btn-cancel  { background:#f3f4f6; color:#374151; }
      .dmm-btn-cancel:hover { background:#e5e7eb; }
      .dmm-list   { flex:1; overflow-y:auto; padding:6px 8px; min-height:80px; }
      .dmm-item   {
        border:1px solid #e5e7eb; border-radius:8px; margin-bottom:6px;
        overflow:hidden; background:#fafafa; transition:border-color .15s;
      }
      .dmm-item:hover { border-color:#9ca3af; }
      .dmm-item-view  { display:flex; align-items:center; gap:8px; padding:8px 10px; }
      .dmm-item-key   {
        font-weight:700; color:#1e3a5f; min-width:60px; max-width:90px;
        overflow:hidden; text-overflow:ellipsis; white-space:nowrap;
        background:#e8f0fb; padding:2px 7px; border-radius:5px; font-size:12px; flex-shrink:0;
      }
      .dmm-item-value {
        flex:1; color:#374151; font-size:12px;
        overflow:hidden; text-overflow:ellipsis; white-space:nowrap;
      }
      .dmm-item-actions { display:flex; gap:4px; flex-shrink:0; }
      .dmm-icon-btn {
        background:none; border:none; cursor:pointer; font-size:14px;
        padding:3px 5px; border-radius:5px; opacity:.6; transition:opacity .15s,background .15s;
      }
      .dmm-icon-btn:hover { opacity:1; background:#f3f4f6; }
      .dmm-item-edit {
        padding:8px 10px; background:#f8faff; border-top:1px solid #e5e7eb;
        display:flex; flex-direction:column; gap:6px;
      }
      .dmm-edit-btns { display:flex; gap:6px; justify-content:flex-end; }
      .dmm-empty      { text-align:center; padding:32px 16px; color:#9ca3af; }
      .dmm-empty-icon { font-size:28px; margin-bottom:8px; }
      .dmm-empty p    { margin:0 0 4px; font-size:13px; color:#6b7280; }
      .dmm-empty small { font-size:11px; color:#9ca3af; }
      .dmm-load-more {
        width:100%; padding:8px; background:#f3f4f6; border:1px dashed #d1d5db;
        border-radius:7px; cursor:pointer; font-size:12px; color:#374151; transition:background .15s;
      }
      .dmm-load-more:hover { background:#e5e7eb; }
      .dmm-footer {
        padding:6px 14px; font-size:11px; color:#9ca3af;
        border-top:1px solid #f3f4f6; flex-shrink:0; min-height:24px;
      }
    `;
    document.head.appendChild(s);
  }
}